export const SITE = {
  name: "JD LEGACY GROUP OF COMPANIES CORP.",
  displayName: "JD Legacy Group of Companies",
  short: "JD Legacy",
  tagline: "BUILD · HOLD · LEGACY",
  blurb:
    "The Philippine parent holding company of five operating companies — technology, real estate, logistics, engineering, and culinary — one legacy, many crafts.",
  logo: "/logo.webp",
  contact: {
    email: "info@jdlegacy.net",
    phone: "0936 948 1844",
    phoneHref: "tel:+639369481844",
  },
  office: {
    label: "Principal office",
    street: "Unit 1 Dimla St., Purok 6 Anyatam",
    city: "San Ildefonso, Bulacan",
    region: "Region\u00A0III (Central\u00A0Luzon)",
    zip: "3010",
    full: "Unit 1 Dimla St., Purok 6 Anyatam, San Ildefonso, Bulacan, Region\u00A0III (Central\u00A0Luzon), 3010",
    /** Line breaks for the About principal-office block. */
    lines: [
      "Unit 1 Dimla St., Purok 6",
      "Anyatam, San Ildefonso,",
      "Bulacan, Region III",
      "(Central Luzon), 3010",
    ],
    mapsUrl:
      "https://www.google.com/maps/search/?api=1&query=Unit+1+Dimla+St+Purok+6+Anyatam+San+Ildefonso+Bulacan",
  },
} as const;

export const SOCIALS = [
  {
    name: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61590511281937",
  },
  {
    name: "TikTok",
    href: "https://www.tiktok.com/@jdlegacygroup",
  },
] as const;

export const PRIMARY_PURPOSE =
  "To invest in, purchase, or otherwise acquire and own, hold, use, sell, assign, transfer, or otherwise dispose of real and personal property of every kind and description, including but not limited to shares of stock, bonds, debentures, notes, and other securities or obligations of any corporation or associations, domestic or foreign; to possess and exercise in respect thereof all the rights, powers and privileges of ownership, including all voting powers of any stock so owned, without however engaging in dealership of securities or stock brokerage.";

export const SECONDARY_PURPOSES = [
  {
    title: "Group support",
    body: "Provide management, administrative, and technical support services to subsidiary companies and affiliates.",
  },
  {
    title: "Subsidiary investment",
    body: "Organize, promote, and invest in subsidiary corporations engaged in information technology, real estate development, delivery and logistics, and other lawful businesses.",
  },
  {
    title: "Intellectual property",
    body: "Own, manage, and protect intellectual property assets, including proprietary software source code, digital systems, and trade secrets.",
  },
  {
    title: "Technology & hardware",
    body: "Engage in software development, IT consultancy, enterprise systems integration, and the maintenance, repair, and distribution of computer hardware, peripherals, and related technological equipment.",
  },
  {
    title: "Real estate",
    body: "Acquire, develop, lease, and hold real estate and landed properties for investment or operations.",
  },
  {
    title: "Delivery & logistics",
    body: "Operate delivery, courier, and logistics services supporting the group and its partners.",
  },
] as const;

export type LeanMedia = {
  src: string;
  widths: readonly number[];
  alt: string;
  width: number;
  height: number;
};

export const MEDIA = {
  parentHolding: {
    src: "/media/parent-holding",
    widths: [960, 1280],
    alt: "A holding-company desk: one parent crest above five operating crafts.",
    width: 1280,
    height: 720,
  },
  fiveCrafts: {
    src: "/media/five-crafts",
    widths: [960, 1280],
    alt: "Five crafts on one table — systems, property, logistics, engineering, and culinary.",
    width: 1280,
    height: 720,
  },
  rooted: {
    src: "/media/rooted",
    widths: [960, 1280],
    alt: "A quiet street in San Ildefonso, Bulacan, near the principal office.",
    width: 1280,
    height: 720,
  },
} as const satisfies Record<string, LeanMedia>;

export const GROUP_COMPANIES = [
  {
    slug: "legacy",
    name: "JD Legacy",
    legalName: "JD LEGACY GROUP OF COMPANIES CORP.",
    tagline: "BUILD, HOLD, LEGACY",
    focus: "Parent holding company",
    role: "parent",
    blurb:
      "The parent holding company — investing in and supporting five operating companies across technology, real estate, logistics, engineering, and culinary.",
    logo: "/logo.webp",
    logoSm: "/logo-32.webp",
    logoMd: "/logo-64.webp",
    href: "/",
    current: true,
    image: MEDIA.parentHolding,
  },
  {
    slug: "integrations",
    name: "JD Integrations",
    legalName: "JD Integrations",
    tagline: "AUTOMATE, INTEGRATE, DOMINATE",
    focus: "Technology & systems",
    role: "operating",
    blurb:
      "The technology operating company — software, ERP, hardware service, and the JD Apps suite.",
    logo: "/jd-integrations-logo.webp",
    logoSm: "/jd-integrations-logo-32.webp",
    logoMd: "/jd-integrations-logo-64.webp",
    href: "https://jdintegrations.net",
    current: false,
    image: {
      src: "/media/craft-integrations",
      widths: [640, 960],
      alt: "A dual-monitor workstation for software and systems work.",
      width: 960,
      height: 540,
    },
  },
  {
    slug: "prime-properties",
    name: "JD Prime Properties",
    legalName: "JD Prime Properties",
    tagline: "PROPERTIES THAT PERFORM",
    focus: "Real estate",
    role: "operating",
    blurb:
      "The real estate operating company — acquire, develop, lease, and hold property under the group.",
    logo: "/jd-prime-properties-logo.webp",
    logoSm: "/jd-prime-properties-logo-32.webp",
    logoMd: "/jd-prime-properties-logo-64.webp",
    href: "https://jdprimeproperties.net",
    current: false,
    image: {
      src: "/media/craft-properties",
      widths: [640, 960],
      alt: "An architectural model and site drawings on a dark wood desk.",
      width: 960,
      height: 540,
    },
  },
  {
    slug: "delivery",
    name: "JD Delivery and Logistics",
    legalName: "JD Delivery and Logistics",
    tagline: "DELIVERING BEYOND BOUNDARIES",
    focus: "Delivery · logistics",
    role: "operating",
    blurb:
      "The delivery and logistics operating company — courier and movement for the group and its partners.",
    logo: "/jd-delivery-logo.webp",
    logoSm: "/jd-delivery-logo-32.webp",
    logoMd: "/jd-delivery-logo-64.webp",
    href: null,
    current: false,
    image: {
      src: "/media/craft-delivery",
      widths: [640, 960],
      alt: "A scale-model van, maps, and packages arranged for logistics work.",
      width: 960,
      height: 540,
    },
  },
  {
    slug: "engineering",
    name: "JD Engineering and Construction",
    legalName: "JD Engineering and Construction",
    tagline: "STRENGTH, STRUCTURE, SUCCESS",
    focus: "Engineering · construction",
    role: "operating",
    blurb:
      "The engineering and construction operating company — physical work planned and built under the group.",
    logo: "/jd-engineering-logo.webp",
    logoSm: "/jd-engineering-logo-32.webp",
    logoMd: "/jd-engineering-logo-64.webp",
    href: null,
    current: false,
    image: {
      src: "/media/craft-engineering",
      widths: [640, 960],
      alt: "Blueprints, a hard hat, and a construction-site model.",
      width: 960,
      height: 540,
    },
  },
  {
    slug: "delight",
    name: "JD Delight Creations",
    legalName: "JD Delight Creations",
    tagline: "DELIGHT IN EVERY CREATIONS",
    focus: "Food · events",
    role: "operating",
    blurb:
      "The culinary and events operating company — food, cakes, and gatherings under the group.",
    logo: "/jd-delight-logo.webp",
    logoSm: "/jd-delight-logo-32.webp",
    logoMd: "/jd-delight-logo-64.webp",
    href: "https://jddelightcreations.net",
    current: false,
    image: {
      src: "/media/craft-delight",
      widths: [640, 960],
      alt: "A plated pastry and coffee service on a dark table.",
      width: 960,
      height: 540,
    },
  },
] as const;

export const PARENT_COMPANY = GROUP_COMPANIES.find((c) => c.role === "parent")!;
export const OPERATING_COMPANIES = GROUP_COMPANIES.filter((c) => c.role === "operating");

export const HOLDING_CHIP = "Parent holding company";

export const HOLDING_BAND = {
  label: HOLDING_CHIP,
  title: "The parent holds the group.",
  lead: `${SITE.short} is not a sixth operating brand. It is the parent holding company — investing in, stewarding, and supporting five operating companies so each craft keeps its own floor.`,
} as const;

export const CONTACT_INTERESTS = [
  "General inquiry",
  "Partnership / referral",
  "JD Integrations",
  "JD Prime Properties",
  "JD Delivery and Logistics",
  "JD Engineering and Construction",
  "JD Delight Creations",
  "Investment / group services",
] as const;

export const CONSULTATION_TOPICS = [
  "Group / holding company",
  "Investment conversation",
  "Partnership / referral",
  "JD Integrations",
  "JD Prime Properties",
  "JD Delivery and Logistics",
  "JD Engineering and Construction",
  "JD Delight Creations",
  "Other",
] as const;

/** Core sectors from the AoI — wording stays valid as subsidiaries grow. */
export const FOCUS_SECTORS =
  "technology, real estate, logistics, engineering, culinary, and related sectors";

export const COMPANIES_HERO = {
  title: "Parent first. Then the crafts.",
  lead: `${SITE.short} is the parent holding company. Five operating companies carry the work — each with its own focus, one shared legacy.`,
  portfolioBlurb:
    "The holding company is the parent. The five companies below are operating sisters — invested in and supported by JD Legacy, not parallel brands to it.",
} as const;

export function formatNameList(names: readonly string[]): string {
  const list = [...names];
  if (list.length === 0) return "";
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")}, and ${list.at(-1)}`;
}

export const SISTERS_COPY = {
  label: "Operating companies",
  title: "Five crafts. One parent.",
  lead: `${formatNameList(OPERATING_COMPANIES.map((co) => co.name))} — sisters under ${SITE.short}, not peers of it.`,
} as const;

export const MANIFESTO =
  "Legacy is not one trade — it is many crafts held together. Technology, property, logistics, engineering, and culinary each have their floor; the parent holds the line.";

export const HERO = {
  chip: HOLDING_CHIP,
  support: `${SITE.short} is the parent holding company of five operating companies across ${FOCUS_SECTORS}.`,
  cta: { href: "/companies", label: "See the group" },
} as const;

export const CAPABILITY_RIBBON = [
  "Parent holding company",
  "Technology",
  "Real estate",
  "Delivery & logistics",
  "Engineering & construction",
  "Culinary",
  "Group support",
  "Investment",
] as const;

export const PILLARS = [
  {
    title: "Hold with purpose",
    body: "The parent’s primary mandate: invest in and steward shares, securities, and property — not securities dealership.",
  },
  {
    title: "Support the floor",
    body: "Management, admin, and technical support so each operating company can focus on its craft.",
  },
  {
    title: "Grow by sector",
    body: "Five operating companies across technology, real estate, logistics, engineering, and culinary — invested in under one parent.",
  },
] as const;
